import { defineStore } from "pinia"
import { useDashboardApi } from "../composables/useDashboardApi"
import {
  activeConnections,
  connectedNodes,
  credentialHealth,
  dashboardRecommendations,
  dashboardSessions,
  dashboardSummary,
  diagnosticsEndpoints,
  emptySummary,
  eventStream,
  relayEndpoints,
  relayPairs,
  stickyAssignments
} from "../fixtures/dashboard"
import type {
  ActiveConnection,
  ConnectedNode,
  CredentialHealth,
  DashboardRecommendation,
  DashboardSession,
  DashboardSummary,
  DiagnosticsEndpoint,
  EventIngestHealth,
  RecentEvent,
  RelayEndpoint,
  RelayPair,
  RuntimeVersion,
  RelaySignals,
  StickyAssignment,
  FixtureScenario
} from "../utils/dashboard-types"
import { dashboardEnvironment } from "../utils/env"

export const scenarioItems: { label: string; value: FixtureScenario }[] = [
  { label: "Ready", value: "ready" },
  { label: "Loading", value: "loading" },
  { label: "Empty", value: "empty" },
  { label: "Error", value: "error" }
]

export type DashboardApiStatus = "idle" | "loading" | "ready" | "error"

const emptyCredentialHealth: CredentialHealth = {
  activeCredentials: 0,
  earliestExpiresAt: null,
  expiringWithin20Minutes: 0,
  refreshEvents: 0,
  revokedCredentials: 0,
  totalCredentials: 0
}

const emptyEventIngest: EventIngestHealth = {
  acceptedBatches: 0,
  duplicateBatches: null,
  hardEvents: 0,
  rejectedBatches: null,
  sanitizationDropCount: null,
  storedEvents: 0
}

const emptyRelaySignals: RelaySignals = {
  eventCount: 0,
  missing: []
}

function fixturesEnabled(): boolean {
  return dashboardEnvironment.dataMode === "fixtures"
}

function fixtureScenarioHasRecords(scenario: FixtureScenario): boolean {
  return scenario !== "empty" && scenario !== "error" && scenario !== "loading"
}

export const useDashboardStore = defineStore("dashboard", {
  actions: {
    async refreshFromApi(dashboardToken?: string, options: { force?: boolean; silent?: boolean } = {}) {
      if (dashboardEnvironment.dataMode === "fixtures") {
        return false
      }

      if (this.refreshInFlight) {
        return false
      }

      if (!options.force && this.apiStatus === "ready" && this.liveSummary) {
        return true
      }

      this.apiErrorMessage = ""
      this.refreshInFlight = true
      if (!options.silent) {
        this.apiStatus = "loading"
      }
      this.lastApiAttemptedAt = Date.now()

      try {
        const snapshot = await useDashboardApi().loadDashboardSnapshot(dashboardToken)
        this.liveConnections = snapshot.connections
        this.liveCredentialHealth = snapshot.credentialHealth
        this.liveDiagnosticsEndpoints = snapshot.diagnosticsEndpoints
        this.liveEventIngest = snapshot.eventIngest
        this.liveEvents = snapshot.events
        this.liveNodes = snapshot.nodes
        this.liveRecommendations = snapshot.recommendations
        this.liveRelayEndpoints = snapshot.relayEndpoints
        this.liveRelayPairs = snapshot.relayPairs
        this.liveRelaySignals = snapshot.relaySignals
        this.liveWorkerVersion = snapshot.workerVersion
        this.liveSessions = snapshot.sessions
        this.liveStickyAssignments = snapshot.stickyAssignments
        this.liveSummary = snapshot.summary
        this.lastUpdatedAt = snapshot.summary.generatedAt
        this.apiStatus = "ready"
        return true
      } catch (error) {
        this.apiErrorMessage = error instanceof Error ? error.message : "Dashboard API request failed."
        if (!options.silent || !this.liveSummary) {
          this.apiStatus = "error"
        }
        this.lastUpdatedAt = Date.now()
        return false
      } finally {
        this.refreshInFlight = false
      }
    },
    setScenario(scenario: FixtureScenario) {
      this.scenario = scenario
      this.lastUpdatedAt = Date.now()
    }
  },
  getters: {
    dataSource: (): "api" | "fixtures" => (fixturesEnabled() ? "fixtures" : "api"),
    connections: (state): ActiveConnection[] =>
      fixturesEnabled()
        ? fixtureScenarioHasRecords(state.scenario)
          ? activeConnections
          : []
        : (state.liveConnections ?? []),
    credentialHealth: (state): CredentialHealth =>
      fixturesEnabled() ? credentialHealth : (state.liveCredentialHealth ?? emptyCredentialHealth),
    diagnosticsEndpoints: (state): DiagnosticsEndpoint[] =>
      fixturesEnabled() ? diagnosticsEndpoints : (state.liveDiagnosticsEndpoints ?? []),
    errorMessage: (state) =>
      fixturesEnabled() && state.scenario === "error"
        ? "Fixture scenario returned an intentional error state."
        : state.apiStatus === "error"
          ? state.apiErrorMessage
          : "",
    eventIngest: (state): EventIngestHealth =>
      fixturesEnabled() ? emptyEventIngest : (state.liveEventIngest ?? emptyEventIngest),
    events: (state) =>
      fixturesEnabled() ? (fixtureScenarioHasRecords(state.scenario) ? eventStream : []) : (state.liveEvents ?? []),
    isApiLoading: (state) => state.apiStatus === "loading",
    isLoading: (state) => (fixturesEnabled() ? state.scenario === "loading" : state.apiStatus === "loading"),
    nodes: (state): ConnectedNode[] =>
      fixturesEnabled() ? (fixtureScenarioHasRecords(state.scenario) ? connectedNodes : []) : (state.liveNodes ?? []),
    relaySignals: (state): RelaySignals =>
      fixturesEnabled() ? emptyRelaySignals : (state.liveRelaySignals ?? emptyRelaySignals),
    sourceLabel: (state): string => {
      if (fixturesEnabled()) {
        return state.scenario === "ready" ? "fixtures" : `fixtures:${state.scenario}`
      }
      if (state.apiStatus === "ready") {
        return "live api"
      }
      if (state.apiStatus === "error") {
        return "api error"
      }
      if (state.apiStatus === "loading") {
        return "api loading"
      }
      return "api idle"
    },
    recommendations: (state) =>
      fixturesEnabled()
        ? fixtureScenarioHasRecords(state.scenario)
          ? dashboardRecommendations
          : []
        : (state.liveRecommendations ?? []),
    relayEndpoints: (state) =>
      fixturesEnabled()
        ? fixtureScenarioHasRecords(state.scenario)
          ? relayEndpoints
          : []
        : (state.liveRelayEndpoints ?? []),
    relayPairs: (state) =>
      fixturesEnabled() ? (fixtureScenarioHasRecords(state.scenario) ? relayPairs : []) : (state.liveRelayPairs ?? []),
    workerVersion: (state): RuntimeVersion | null => (fixturesEnabled() ? null : state.liveWorkerVersion),
    sessions: (state) =>
      fixturesEnabled()
        ? fixtureScenarioHasRecords(state.scenario)
          ? dashboardSessions
          : []
        : (state.liveSessions ?? []),
    stickyAssignments: (state) =>
      fixturesEnabled()
        ? fixtureScenarioHasRecords(state.scenario)
          ? stickyAssignments
          : []
        : (state.liveStickyAssignments ?? []),
    summary: (state): DashboardSummary =>
      fixturesEnabled()
        ? state.scenario === "empty"
          ? emptySummary
          : dashboardSummary
        : (state.liveSummary ?? { ...emptySummary, generatedAt: state.lastUpdatedAt, panels: [], stats: [] })
  },
  state: () => ({
    apiErrorMessage: "",
    apiStatus: "idle" as DashboardApiStatus,
    lastApiAttemptedAt: 0,
    lastUpdatedAt: dashboardSummary.generatedAt,
    liveConnections: null as ActiveConnection[] | null,
    liveCredentialHealth: null as CredentialHealth | null,
    liveDiagnosticsEndpoints: null as DiagnosticsEndpoint[] | null,
    liveEventIngest: null as EventIngestHealth | null,
    liveEvents: null as RecentEvent[] | null,
    liveNodes: null as ConnectedNode[] | null,
    liveRecommendations: null as DashboardRecommendation[] | null,
    liveRelayEndpoints: null as RelayEndpoint[] | null,
    liveRelayPairs: null as RelayPair[] | null,
    liveRelaySignals: null as RelaySignals | null,
    liveWorkerVersion: null as RuntimeVersion | null,
    liveSessions: null as DashboardSession[] | null,
    liveStickyAssignments: null as StickyAssignment[] | null,
    liveSummary: null as DashboardSummary | null,
    refreshInFlight: false,
    scenario: "ready" as FixtureScenario
  })
})
